import { Icon } from "@vds/icons";
import { RadioButton, RadioButtonGroup } from "@vds/radio-buttons";
import { Checkbox } from "@vds/checkboxes";
import { Notification } from "@vds/notifications";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCameraCaliration } from "../../../../api/RoiServiceManager";
import { BasicSnapshot } from "../../../../global/components/snapshots/BasicSnapshot";
import ManualCalibration from "../../../../pages/CameraOnboarding/manualCalibration";
import SnapshotComponent from "../../../../pages/CameraOnboarding/SnapshotComponent";
import { getAllCameraAndFovsDevices, refreshCameraPage, resetOnboardingCamera, showCalibrationBanner, showNotificationBanner, updateCameraCalibrationStatus } from "../../../../store/actions";
import {
  StreamDescriptiveText,
  VisionStreamCalibrationSnapshot,
  VisionStreamCalibrationSnapshotItem,
  CalibrationRowContainer,
  CalibrationSnapshotCol,
  CalibrationRadioCol,
  CalibrationSearchCol,
  SearchCheckboxRow,
  StreamNameSyncRow,
  StreamItemContainer
} from "../styles";
import ExampleModal from "./ExampleModal";
import { HTTP_STATUS_CODES } from "../../../../global/constants/constants";
import { CALIBRATE_CONSTNATS, RADIO_LABELS, NOTIFICATION_TYPES, TITLES, SUBTITLES, BUTTON_LABELS } from "../../../../constants/cameraInfoConstants";
import { CVAPPS_LICENSE_TYPE_MESSAGES } from "../../../../pages/CameraOnboarding/cameraOnboardingConstants";
import { Box, Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import ErrorTemplate from "../../../../global/components/errorTemplate/ErrorTemplate";
import { Body } from "@vds/typography";
import { useEnableSearchState } from "../../hooks/useEnableSearchState";
import { useHistoricalSearchEligibility } from "../../hooks/useHistoricalSearchEligibility";

const StreamCalibrationModal = ({
  showExampleModal,
  setShowExampleModal,
  handleOnBack,
  fovDatas,
  companionDevice,
  handleNextCalibrationSteps,
  onNextDisableChange,
  setHandleNextCalibrationSteps,
  onClose,
  setShowManualCalibration,
  manualCount,
  setmanualCount,
  manualDevices,
  setManualDevices,
  showManualBack,
  setShowManualBack,
  setIsLoading,
  setFovIdsCount,
  onCalibrationComplete,
  initialEnableSearch,
  isHistoricalSearchEligible,
  forceManual = false
}) => {
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const { selectedCamQR } = useSelector((state) => state.cameraOnboardingReducer);
  const storeFovList = useSelector((state) => state.deviceReducer?.fovList);
  const { cameraList = [] } = useSelector((state) => state.deviceReducer);
  const isSearchEligible = useHistoricalSearchEligibility(companionDevice?.id);
  const selectedCamera = cameraList.find((cam) => cam?.qrcode === selectedCamQR);
  const existingCalibration = selectedCamera?.customdata?.calibrationConfig;
  const fovIds = fovDatas;
  const companionQrcode = companionDevice?.qrcode;
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isManual, setIsManual] = useState(false);
  const [manualSnapshot, setManualSnapshot] = useState([]);
  const [farposition, setFarPosition] = useState([]);
  const [nearSize, setNearSize] = useState([]);
  const [farSize, setFarSize] = useState([]);
  const [selectdeviceId, setSelectDeviceId] = useState("");
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [refreshButtonClicked, setRefreshButtonClicked] = useState(false);
  const [floorImageUrl , setFloorImageUrl] = useState("");
  const [enableSearchState, setEnableSearchState] = useState(() =>
    Object.fromEntries((initialEnableSearch || []).map(({ fovId, enableSearch }) => [fovId, !!enableSearch]))
  );

  const isHsEnabled = window.REACT_APP_HISTORICAL_SEARCH_ENABLED === "true";
  const fovRefetchFns = useRef({});

  const { effectiveAvailable } = useEnableSearchState({
    editFlow: false,
    cameraId: selectedCamera?.id,
    enabledItems: Object.values(enableSearchState).map((enabled) => ({ enableSearch: enabled })),
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [banner, setBanner] = useState(false);

  const [floorImageObj, setFloorImageObj] = useState([]);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (typeof setFovIdsCount === "function") {
      setFovIdsCount(fovIds?.length || 0);
    }
  }, [fovIds]);

  useEffect(() => {
    if (handleNextCalibrationSteps) {
      handleNext(manualCount);
      setHandleNextCalibrationSteps(false);
    }
  }, [handleNextCalibrationSteps]);

  // When calibration is forced to manual (e.g. required by a specific CV app), skip the
  // automatic/manual choice entirely and jump straight into manual calibration for this FOV.
  // This must NOT wait for the snapshot to finish loading (floorImageObj) — snapshot fetch
  // is an async API call that can be slow, so gating on it here created a race where the
  // radio-choice screen would flash with no options (forceManual hides the radio group too).
  useEffect(() => {
    if (!forceManual || !fovIds.length) return;
    const fovId = fovIds[0]?.fovId;
    if (!manualDevices.includes(fovId)) {
      setManualDevices([fovId]);
    }
    setSelectDeviceId(fovId);
    setSelectedOptions([`${fovId}-manual`]);
    setIsManual(true);
    // Only one device in forced-manual mode — mark it as already "queued" so Submit
    // saves the calibration directly instead of re-entering the manual-device queue.
    setmanualCount(1);
  }, [forceManual, fovIds]);

  // Feed the snapshot into ManualCalibration's image list as soon as it loads —
  // decoupled from the effect above so manual mode doesn't have to wait for it.
  useEffect(() => {
    if (!forceManual || !fovIds.length) return;
    const fovId = fovIds[0]?.fovId;
    const fovEntry = floorImageObj.find((obj) => obj.fovId === fovId);
    if (!fovEntry) return;
    setManualSnapshot([{ id: fovId, name: fovEntry.fovName, imageurl: fovEntry.url }]);
  }, [forceManual, floorImageObj, fovIds]);

  // Whenever the manual calibration screen is showing (forceManual jump, or the user picked
  // "Manual" from the radio choice), the Calibrate/Next button must stay disabled until the
  // snapshot for the device currently being calibrated has actually loaded. calibrationOption's
  // disabled flag only guards the radio choice itself — once "Manual" is selected there was
  // nothing stopping the user from hitting Calibrate/Next against a blank/failed scene.
  useEffect(() => {
    if (typeof onNextDisableChange !== "function") return;
    if (!isManual) {
      onNextDisableChange(false);
      return;
    }
    const hasSnapshot = manualSnapshot.some(
      (snap) => snap?.id === selectdeviceId && Boolean(snap?.imageurl)
    );
    onNextDisableChange(!hasSnapshot);
  }, [isManual, manualSnapshot, selectdeviceId, onNextDisableChange]);

  const handleNext = async (count = 0) => {
    const operations = [];
    let payload = {};
    const parentCameraId = storeFovList?.find((fov) => fov.id === fovIds[0]?.fovId)?.parentdeviceid;
    if (selectedOptions.length === 0) {
      fovIds.forEach((item) => {
        selectedOptions.push(`${item?.fovId}-automatic`);
      });
      setSelectedOptions(selectedOptions);
    }
    if (selectedOptions.length) {
      for (let i = selectedOptions.length - 1; i >= 0; i--) {
        if (selectedOptions[i] === undefined || selectedOptions[i] === null || selectedOptions[i] === "") {
          selectedOptions.splice(i, 1);
        }
      }
      if (selectedOptions.length < fovIds.length) {
        const selectedOptionsIds = selectedOptions.map((item) => item.replace(/-(automatic|manual|skipStreamCalibration)$/, ""));
        const fovIdsWithoutOption = fovIds.filter((item) => !selectedOptionsIds.includes(item?.fovId));
        fovIdsWithoutOption.forEach((item) => {
          selectedOptions.push(`${item?.fovId}-automatic`);
        });
      }
      const allSkip = selectedOptions.every((option) => option.includes("skipStreamCalibration"));
      const manual = selectedOptions.some((option) => option.includes("-manual"));
      if (manual && manualDevices && manualCount < manualDevices.length) {
        setShowManualCalibration(true);
        setSelectDeviceId(manualDevices[manualCount]);
        setmanualCount(count + 1);
        setIsManual(true);
      } else if (allSkip) {
        setBanner(true);
        if (onCalibrationComplete) {
          dispatch(getAllCameraAndFovsDevices());
          await onCalibrationComplete(null);
          onClose(true);
        } else {
          dispatch(resetOnboardingCamera());
          dispatch(getAllCameraAndFovsDevices());
          dispatch(refreshCameraPage(true));
          navigate("/devices", {
            state: {
              banner: true,
              deviceId: parentCameraId
            }
          });
        }
      } else {
        selectedOptions.forEach((option) => {
          if (option && (option.includes("-automatic") || option.includes("-manual"))) {
            let deviceid;
            let config;
            if (option.includes("-automatic")) {
              deviceid = option.split("-automatic")[0];
            } else {
              deviceid = option.split("-manual")[0];
              let nearSizeDefault = [{ nearSize: parseInt(50 * 100), id: deviceid }];
              let farSizeDefaut = [{ farSize: parseInt(40 * 100), id: deviceid }];
              const farpositionM = farposition.filter((f) => f.id === deviceid);
              const farSizeM = farSize.filter((f) => f.id === deviceid);
              const nearSizeM = nearSize.filter((f) => f.id === deviceid);
              config = {
                farposition: farpositionM[0]?.farposition ? farpositionM[0]?.farposition : 100,
                sceneparameters: {
                  far_size: farSizeM[0]?.farSize ? farSizeM[0]?.farSize : farSizeDefaut[0]?.farSize,
                  near_size: nearSizeM[0]?.nearSize ? nearSizeM[0]?.nearSize : nearSizeDefault[0]?.nearSize
                }
              };
            }
            payload = {
              accountname: activeSession.accountname,
              resources: [
                {
                  fovidentifier: {
                    deviceid: deviceid
                  },
                  config
                }
              ]
            };
            operations.push(setCameraCaliration(payload, activeSession));
          }
        });
      }
    }
    if (operations.length > 0) {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      try {
        setIsLoading(true);
        const results = await Promise.allSettled(operations);
        const anyCalibrationSucceeded = results.some(
          (r) =>
            r.status === "fulfilled" &&
            (r.value === HTTP_STATUS_CODES.OK || r.value === HTTP_STATUS_CODES.ACCEPT)
        );
        if (anyCalibrationSucceeded) {
          setBanner(true);
          const calibrationNotificationInfo = {
            type: NOTIFICATION_TYPES.STREAM_CALIBRATION,
            priority: NOTIFICATION_TYPES.INFO,
            data: {
              title: CVAPPS_LICENSE_TYPE_MESSAGES.REGISTRATION_TITLE,
              subtitle: SUBTITLES.STREAM_CALIBRATION,
              buttonLabel: BUTTON_LABELS.REFRESH,
              redirectUrl: "",
            },
            isDismissible: false,
            hideCloseButton: false,
            dispatchAction: getAllCameraAndFovsDevices(),
            cameraInfoDispatchAction: refreshCameraPage(true),
            deviceId: parentCameraId,
            showNotification: true,
          };
          dispatch(showCalibrationBanner(calibrationNotificationInfo));
          if (onCalibrationComplete) {
            const licenseResult = await onCalibrationComplete(enableSearchState);
            dispatch(updateCameraCalibrationStatus(false));
            if (licenseResult?.anyFailed) {
              dispatch(showNotificationBanner({
                type: "Failed to Calibrate",
                priority: "warning",
                data: {
                  title: "Calibration succeeded but couldn't enable search",
                  subTitle: "Search could not be enabled for the selected streams. Please try again from camera settings."
                },
                showNotification: true
              }));
            }
            onClose(true);
          } else {
            dispatch(refreshCameraPage(true));
            navigate("/devices", {
              state: {
                banner: true,
                deviceId: parentCameraId
              }
            });
          }
          setIsManual(false);
          setShowManualCalibration(false);
        } else {
          onClose();
          dispatch(showCalibrationBanner({ showNotification: false }));
          dispatch(showNotificationBanner({
            type: "Failed to Calibrate",
            priority: forceManual ? "critical" : "warning",
            data: {
              title: "Calibration failed",
              subTitle: isHistoricalSearchEligible
                ? "Calibration failed and search could not be enabled for the selected streams. Please try calibrating the camera again."
                : "Please try calibrating the camera after some time again."
            },
            showNotification: true
          }));
          if (!forceManual && onCalibrationComplete) {
            onCalibrationComplete(null);
          }
        }
        setIsManual(false);
        setShowManualCalibration(false);
        setIsLoading(false);
      } catch (error) {
        console.error("Error calibrating camera:", error);
      } finally {
        isSubmittingRef.current = false;
      }
    }
  };

  const handleRadioChange = (value, index, name, id, floorImageObj) => {
    if (value.includes("-manual")) {
      const deviceid = value.split("-manual")[0];
      if (!manualDevices.includes(deviceid)) {
        manualDevices.push(deviceid);
        setManualDevices(manualDevices);
      }
        const tempManualSnapshot = [];
        floorImageObj.map((item) => {
          tempManualSnapshot.push({
            id: item.fovId,
            name: item.fovName,
            imageurl: (floorImageUrl === "") ? item?.url : floorImageUrl
          });
        });
        setManualSnapshot(tempManualSnapshot);
    } else {
      if (value.split("-automatic")[0]) {
        const arr = manualDevices.filter((item) => item !== value.split("-automatic")[0]);
        setManualDevices(arr);
        setManualSnapshot(manualSnapshot.filter((f) => f.id !== value.split("-manual")[0]));
      }
      setIsManual(false);
    }
    setSelectedOptions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const calibrationOption = (item) => {
    const options = [
      {
        children: "Automatic",
        value: `${item?.fovId}-automatic`,
        ariaLabel: "radio one",
        disabled: false
      },
    ];

    const fovHasImage = floorImageObj.some((obj) => obj.fovId === item?.fovId);
    const manualCalibrationOption = {
      children: !fovHasImage ? RADIO_LABELS.MANUAL_NO_SNAPSHOT : "Manual",
      value: `${item?.fovId}-manual`,
      ariaLabel: "radio two",
      disabled: !fovHasImage
    };
    options.splice(1, 0, manualCalibrationOption);
    return options;
  };

  useEffect(() => {
    if (showManualBack) {
      handleBack();
      setShowManualBack(false);
    }
  }, [showManualBack]);

  const handleBack = () => {
    if (isManual) {
      let count = manualCount > 0 ? manualCount - 1 : 0;
      setmanualCount(count);
      if (count === 0) {
        setIsManual(false);
        setSelectDeviceId([]);
        setManualDevices([]);
        setManualSnapshot([]);
      }
      if (manualDevices.length === manualCount) {
        count = count - 1;
      }
      setSelectDeviceId(manualDevices[count]);
      setShowManualCalibration(false);
      setSelectedOptions([]);
    } else {
      navigate("/devices");
    }
  };

  const exampleModal = () => {
    setShowExampleModal(true);
  };
  const updateSnapshot = (url, i) => {
    const tempManualSnapshot = [];
    setFloorImageObj((prevData) => {
      setIsImageLoaded(true);
      const exists = prevData.some((item) => item.fovId === i.fovId);
      if(exists){
        setFloorImageUrl(url);
        tempManualSnapshot.push({
          id: i.fovId,
          name: i.fovName,
          imageurl: url
        });
        setManualSnapshot(tempManualSnapshot);
      }
      if (exists) return prevData;
      return [...prevData, { ...i, url }];
    });
  };

  useEffect(() => {
    if (refreshButtonClicked) {
      setIsImageLoaded(false);
      setRefreshButtonClicked(false);
    }
  }, [refreshButtonClicked]);

  return (
    <>
      {!showExampleModal ? (
        <>
          {isManual && (
            <>
              {!manualSnapshot.some(
                (snap) => snap?.id === selectdeviceId && Boolean(snap?.imageurl)
              ) ? (
                  <>
                    <Box sx={{ height: "70vh" }}>
                      <ErrorTemplate
                        title={CALIBRATE_CONSTNATS.SNAPSHOT_UNAVAILABLE}
                        description={CALIBRATE_CONSTNATS.SNAPSHOT_DESC}
                        templateType="error"
                      />
                    </Box>
                  </>
              ) : (
                <div>
                  <ManualCalibration
                    setFarPosition={setFarPosition}
                    farposition={farposition}
                    deviceid={selectdeviceId}
                    farSize={farSize}
                    setFarSize={setFarSize}
                    nearSize={nearSize}
                    setNearSize={setNearSize}
                    imageUrl={
                      manualSnapshot.find((snap) => snap?.id === selectdeviceId)?.imageurl
                    }
                    pageName="pairingFlow"
                    existingCalibrationConfig={existingCalibration}
                    resizeOnWindowChange={forceManual}
                  />
                </div>
              )}
              {/* forceManual jumps straight into manual mode without waiting for the snapshot
                  fetch to resolve — keep this mounted off-screen so the fetch still runs and
                  feeds manualSnapshot (via the effect above) once it completes. */}
              {forceManual && fovIds?.length > 0 && (
                <div style={{ display: "none" }}>
                  <SnapshotComponent
                    component={BasicSnapshot}
                    editFlow={true}
                    fovObj={fovIds[0]}
                    selectedCameraQR={selectedCamQR}
                    companionSelected={companionQrcode}
                    height="218px"
                    width="328px"
                    showIcon={true}
                    handleChange={(url) => {
                      updateSnapshot(url, fovIds[0]);
                    }}
                    refreshImage={true}
                    title={fovIds[0]?.fovName}
                    setRefreshButtonClicked={setRefreshButtonClicked}
                  />
                </div>
              )}
            </>
          )}{" "}
          {!isManual && (
            <>
              {!forceManual && (
                <StreamDescriptiveText>
                  Stream calibration helps improve the accuracy of your computer vision apps. We can automatically calibrate your streams or you can calibrate
                  manually. <br /> <br /> Note: Automatic calibration works best when there are people in various locations of the stream’s view or if there is a
                  single person moving around to different points within the stream’s view.{" "}
                  <a style={{ textDecoration: "underLine", cursor: "pointer" }} onClick={exampleModal}>
                    See example
                  </a>
                </StreamDescriptiveText>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginTop: "24px" }}>
                {fovIds.map((i, index) => (
                    <StreamItemContainer key={index}>
                      <CalibrationRowContainer>
                        {/* Col 1: snapshot — refreshImage=false hides built-in title+sync */}
                        <CalibrationSnapshotCol>
                          <SnapshotComponent
                            component={BasicSnapshot}
                            editFlow={true}
                            fovObj={i}
                            selectedCameraQR={selectedCamQR}
                            companionSelected={companionQrcode}
                            height="218px"
                            width="328px"
                            showIcon={true}
                            handleChange={(url) => {
                              updateSnapshot(url, i);
                            }}
                            refreshImage={false}
                            setRefreshButtonClicked={setRefreshButtonClicked}
                            onRefetchReady={(fn) => { fovRefetchFns.current[i.fovId] = fn; }} // stable: BasicSnapshot calls this once on mount
                          />
                        </CalibrationSnapshotCol>
                        {/* Col 2: Radio buttons */}
                        {!forceManual && (
                          <CalibrationRadioCol>
                            <RadioButtonGroup
                              value={selectedOptions[index] || ""}
                              onChange={(e) => handleRadioChange(e.target.value, index, i.fovName, i.fovId, floorImageObj)}
                              error={false}
                              data={calibrationOption(i, index)}
                            />
                          </CalibrationRadioCol>
                        )}
                        {/* Col 3: Enable Search — pushed to far right */}
                        {isSearchEligible && (
                          <CalibrationSearchCol>
                            <SearchCheckboxRow>
                              <div style={{ width: "fit-content" }}>
                                <Checkbox
                                  selected={!!enableSearchState[i.fovId]}
                                  disabled={effectiveAvailable === 0 && !enableSearchState[i.fovId]}
                                  onChange={() => setEnableSearchState((prev) => ({ ...prev, [i.fovId]: !prev[i.fovId] }))}
                                />
                              </div>
                              <span style={{ fontWeight: "400", fontSize: "16px" }}>Enable Search</span>
                              <Tooltip title="Enabling Search will activate data collection on this stream, allowing you to search footage from this camera." placement="top" PopperProps={{ disablePortal: true }}>
                                <InfoOutlined fontSize="small" style={{ color: "#6f7171", cursor: "pointer" }} />
                              </Tooltip>
                            </SearchCheckboxRow>
                            {effectiveAvailable !== null && (
                              <Body size="small" viewport="desktop" primitive="span" color="#6f7171" style={{ marginBottom: "0px" }}>
                                {effectiveAvailable === 1 ? "1 license is available" : `${effectiveAvailable} licenses are available`}
                              </Body>
                            )}
                          </CalibrationSearchCol>
                        )}
                      </CalibrationRowContainer>
                      {/* Stream name + sync — separate div, close to its snapshot */}
                      <StreamNameSyncRow
                        data-testid="devices_cameraRefreshSnapshot"
                        onClick={() => {
                          setRefreshButtonClicked(true);
                          fovRefetchFns.current[i.fovId]?.();
                        }}
                      >
                        <Body size="large" bold>{i?.fovName}</Body>
                        <Icon name="sync" size="small" color="#000000" />
                      </StreamNameSyncRow>
                    </StreamItemContainer>
                  ))}
              </div>
            </>
          )}
        </>
      ) : (
        <ExampleModal handleOnBack={handleOnBack} />
      )}
    </>
  );
};

export default StreamCalibrationModal;
